/**
 * One autopilot parameter at a time.
 *
 * Reading is view-level. Writing takes control of the drone AND the tactical
 * role, because a parameter write changes how the autopilot behaves when nobody
 * is looking — arming checks, failsafe actions, geofence enforcement all live
 * here. The server enforces that; this module only asks.
 */

import { api, type ApiResult } from '@/api/client';
import type { CommandResult } from '@/types/domain';

export interface ParameterRead {
  droneId: string;
  name: string;
  ok: boolean;
  /** Null when the aircraft has no such value, or could not be reached. */
  value: number | null;
  message: string;
}

export function readParameter(droneId: string, name: string): Promise<ApiResult<ParameterRead>> {
  return api.get<ParameterRead>(
    `/parameters/${encodeURIComponent(droneId)}/${encodeURIComponent(name)}`,
    { logged: false },
  );
}

export interface ParameterRow {
  name: string;
  value: number;
  type: string;
  /** The aircraft's own index, or null when it never said. */
  index: number | null;
}

export interface ParameterPage {
  droneId: string;
  fetchedAt: number;
  /** How many matched the search across the whole set, not on this page. */
  matched: number;
  offset: number;
  limit: number;
  /**
   * Whether the WHOLE fetch arrived — never the page and never the search.
   * A search that found what it was looking for inside an incomplete set is
   * still an incomplete set, and a parameter absent because a radio dropped it
   * looks exactly like one the airframe does not have. Rendering `complete`
   * as if it described the page is the one way to make this panel lie.
   */
  complete: boolean;
  expected: number | null;
  received: number;
  /** A bounded sample of the indices that never arrived. */
  missing: number[];
  missingCount: number;
  message: string;
  parameters: ParameterRow[];
}

/** What one refresh got, without the values. */
export type ParameterFetchResult = Omit<
  ParameterPage,
  'matched' | 'offset' | 'limit' | 'parameters'
>;

/**
 * Ask the aircraft to stream its entire parameter set.
 *
 * Control-gated even though it writes nothing, and deliberately not called on
 * mount: on a 57600 baud radio this is minutes of link the telemetry does not
 * get. Opening the panel must not cost an operator their map.
 */
export function refreshParameters(droneId: string): Promise<ApiResult<ParameterFetchResult>> {
  return api.post<ParameterFetchResult>(
    `/parameters/${encodeURIComponent(droneId)}/refresh`,
    {},
    { label: `PARAM FETCH ${droneId}` },
  );
}

/** Browse the last fetched set. 404 until a refresh has happened. */
export function listParameters(
  droneId: string,
  options: { search?: string; limit?: number; offset?: number } = {},
): Promise<ApiResult<ParameterPage>> {
  const query = new URLSearchParams();
  if (options.search) query.set('search', options.search);
  query.set('limit', String(options.limit ?? 200));
  query.set('offset', String(options.offset ?? 0));
  return api.get<ParameterPage>(
    `/parameters/${encodeURIComponent(droneId)}?${query.toString()}`,
    { logged: false },
  );
}

export function writeParameter(
  droneId: string,
  name: string,
  value: number,
): Promise<ApiResult<{ droneId: string; name: string; result: CommandResult }>> {
  return api.put<{ droneId: string; name: string; result: CommandResult }>(
    `/parameters/${encodeURIComponent(droneId)}/${encodeURIComponent(name)}`,
    { value },
    { label: `PARAM ${name.toUpperCase()}` },
  );
}
